import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api/client.js';
import { TableSkeleton, Empty, ErrorBanner, Stat, StatGroup } from '../../components/ui/Primitives.js';
import { Icon } from '../../components/ui/Icon.js';
import { relativeTime } from '../../lib/format.js';

/**
 * The fleet: the switches, the host they run on, and the database behind them.
 *
 * Designed for a handful of nodes, not a hyperscaler — there is one today and
 * there will be a few. That rules out most of what a fleet page usually grows:
 * no map, no topology, no sparklines (a chart of one series is a number with
 * decoration around it), no pagination for eight rows.
 *
 * READ-ONLY, and that is a decision. There is no inbound path to a node —
 * arc-agent binds loopback "always" — and the deployed OpenSIPS has no
 * dispatcher module, so nothing consumes `nodes.status`. A drain button would
 * set a column read by one line checking for 'retired', and report success: a
 * button that lies.
 */

interface Assertion { id: number; name: string; ok: boolean; detail: string }
interface NodeRow {
  id: string; name: string; role: string; region: string; status: string;
  public_ip: string | null;
  liveness: 'up' | 'down' | 'never'; seconds_since_beat: number | null;
  last_heartbeat_at: string | null;
  active_calls: number | null; registrations: number | null; cdr_spool_depth: number | null;
  load_1: number | null; load_5: number | null;
  mem_total_mb: number | null; mem_used_mb: number | null;
  disk_pct: number | null; uptime_sec: number | null;
  services: Record<string, string>; assertions: Assertion[];
  agent_version: string | null; failing_assertions: number; homed_tenants: number;
}
interface DbHealth {
  size: string | null; connections: number; max_connections: number;
  cache_hit_pct: number | null; oldest_txn_sec: number | null;
  committed: number; rolled_back: number; rtt_ms: number;
}

function duration(sec: number | null): string {
  if (sec == null) return '—';
  const d = Math.floor(sec / 86400);
  if (d >= 1) return `${d}d`;
  const h = Math.floor(sec / 3600);
  if (h >= 1) return `${h}h`;
  return `${Math.max(1, Math.floor(sec / 60))}m`;
}

/** Anything that is not "active" is worth reading; "active" is the wallpaper. */
function serviceTone(state: string): string | undefined {
  if (state === 'active') return undefined;
  if (state === 'activating' || state === 'reloading') return 'var(--arc-warn)';
  return 'var(--arc-danger)';
}

export function NodesPage() {
  const q = useQuery({
    queryKey: ['platform-nodes'],
    queryFn: () => api.get<{ data: NodeRow[]; database: DbHealth }>('/v1/platform/nodes'),
    // Inside the 30s beat interval, so a node going quiet shows up within a beat
    // of it mattering. No websocket: the hub is per-tenant and this is one page
    // with an audience of one.
    refetchInterval: 10_000,
  });

  if (q.error) return <ErrorBanner error={q.error} />;

  const nodes = q.data?.data ?? [];
  const db = q.data?.database;
  const down = nodes.filter((n) => n.liveness === 'down');
  const never = nodes.filter((n) => n.liveness === 'never');
  const failing = nodes.filter((n) => n.failing_assertions > 0);

  return (
    <>
      <div className="arc-page-head">
        <div>
          <h1 className="arc-h1">Nodes</h1>
          <p className="arc-muted" style={{ marginTop: 3 }}>
            The switches carrying calls, and the platform behind them.
          </p>
        </div>
      </div>

      {down.length > 0 && (
        <div className="arc-banner arc-banner-danger" style={{ marginBottom: 14 }}>
          <Icon name="warning" size={14} />
          <span>
            {down.map((n) => n.name).join(', ')} stopped reporting.
            Calls already up are unaffected — media is between FreeSWITCH and
            rtpengine — but new registrations and config changes are not landing.
          </span>
        </div>
      )}
      {never.length > 0 && (
        <div className="arc-banner arc-banner-warn" style={{ marginBottom: 14 }}>
          <Icon name="warning" size={14} />
          <span>
            {never.map((n) => n.name).join(', ')} has never sent a heartbeat.
            Either the agent is not running, or it has no ARC_API_URL and
            ARC_NODE_TOKEN.
          </span>
        </div>
      )}

      {db && (
        <div style={{ marginBottom: 14 }}>
          <StatGroup>
            {/*
              Round-trip latency to Ireland, measured on this request rather than
              assumed. ADR-001 budgets ~183ms per statement and the whole
              read-model design follows from that number; if it doubles,
              everything chatty gets slower and nothing else would say so.
            */}
            <Stat bare icon="server" hue={1} label="Database round trip"
              value={`${db.rtt_ms} ms`}
              tone={db.rtt_ms > 400 ? 'warn' : undefined}
              sub={db.size ? `${db.size} on disk` : 'Supabase, eu-west-1'} />
            <Stat bare icon="dashboard" hue={2} label="Connections"
              value={`${db.connections} / ${db.max_connections}`}
              tone={db.max_connections > 0 && db.connections / db.max_connections > 0.8 ? 'danger' : undefined}
              sub="against the pooler" />
            <Stat bare icon="rates" hue={4} label="Cache hit"
              value={db.cache_hit_pct == null ? '—' : `${db.cache_hit_pct}%`}
              tone={db.cache_hit_pct != null && db.cache_hit_pct < 95 ? 'warn' : undefined}
              sub="reads served from memory" />
            <Stat bare icon="warning" hue={3} label="Failing checks"
              value={failing.reduce((n, x) => n + x.failing_assertions, 0)}
              tone={failing.length > 0 ? 'danger' : undefined}
              sub={failing.length > 0 ? 'see the node below' : 'all nodes passing'} />
          </StatGroup>
        </div>
      )}

      {q.isLoading ? (
        <div className="arc-card"><TableSkeleton rows={2} cols={4} /></div>
      ) : nodes.length === 0 ? (
        <div className="arc-card">
          <Empty icon="server" title="No nodes enrolled"
            hint="tools/enrol-node.mjs registers a node and issues its token." />
        </div>
      ) : nodes.map((n) => (
        <div key={n.id} className="arc-card" style={{ marginBottom: 14 }}>
          <div className="arc-card-head">
            <div className="arc-row" style={{ gap: 10 }}>
              <h2 className="arc-h2">{n.name}</h2>
              {/* Liveness is the only thing on this header that gets colour.
                  "up" is every node's normal state and needs no pill. */}
              {n.liveness === 'up' ? (
                <span className="arc-subtle" style={{ fontSize: 12 }}>
                  beat {n.seconds_since_beat}s ago
                </span>
              ) : (
                <span style={{
                  color: n.liveness === 'never' ? 'var(--arc-warn)' : 'var(--arc-danger)',
                  fontWeight: 550, fontSize: 12.5,
                }}>
                  {n.liveness === 'never' ? 'never reported'
                    : `silent for ${duration(n.seconds_since_beat)}`}
                </span>
              )}
            </div>
            <span className="arc-subtle arc-mono" style={{ fontSize: 11.5 }}>
              {n.role} · {n.region}{n.public_ip ? ` · ${n.public_ip}` : ''}
              {n.agent_version ? ` · agent ${n.agent_version}` : ''}
            </span>
          </div>

          <div className="arc-card-body">
            <dl className="arc-kv" style={{ gridTemplateColumns: 'auto 1fr auto 1fr' }}>
              <dt>Calls</dt><dd className="arc-num">{n.active_calls ?? '—'}</dd>
              <dt>Registrations</dt><dd className="arc-num">{n.registrations ?? '—'}</dd>
              <dt>Load</dt>
              <dd className="arc-num">
                {n.load_1 ?? '—'}{n.load_5 != null && <span className="arc-subtle"> / {n.load_5}</span>}
              </dd>
              <dt>Memory</dt>
              <dd className="arc-num">
                {n.mem_used_mb != null && n.mem_total_mb
                  ? `${Math.round((n.mem_used_mb / n.mem_total_mb) * 100)}%`
                  : '—'}
                <span className="arc-subtle">
                  {n.mem_used_mb != null && n.mem_total_mb
                    ? ` (${n.mem_used_mb} of ${n.mem_total_mb} MB)` : ''}
                </span>
              </dd>
              <dt>Disk</dt>
              <dd className="arc-num" style={n.disk_pct != null && n.disk_pct > 85
                ? { color: 'var(--arc-danger)', fontWeight: 550 } : undefined}>
                {n.disk_pct != null ? `${n.disk_pct}%` : '—'}
              </dd>
              <dt>Uptime</dt><dd>{duration(n.uptime_sec)}</dd>
              <dt>Customers</dt><dd className="arc-num">{n.homed_tenants}</dd>
              {/* Unshipped CDRs are unbilled calls. Worth its own line, and worth
                  colour the moment it is not zero. */}
              <dt>CDR backlog</dt>
              <dd className="arc-num" style={n.cdr_spool_depth
                ? { color: 'var(--arc-warn)', fontWeight: 550 } : undefined}>
                {n.cdr_spool_depth ?? '—'}
              </dd>
            </dl>

            {Object.keys(n.services).length > 0 && (
              <>
                <div className="arc-label" style={{ marginTop: 14 }}>Services</div>
                <div className="arc-row" style={{ gap: 16, flexWrap: 'wrap', marginTop: 4 }}>
                  {Object.entries(n.services).map(([name, state]) => (
                    <span key={name} style={{ fontSize: 12.5 }}>
                      <span className="arc-mono">{name}</span>{' '}
                      <span style={{ color: serviceTone(state), fontWeight: state === 'active' ? 400 : 550 }}>
                        {state}
                      </span>
                    </span>
                  ))}
                </div>
              </>
            )}

            {n.assertions.length > 0 && (
              <>
                <div className="arc-label" style={{ marginTop: 14 }}>
                  Startup checks
                  {n.failing_assertions === 0 && (
                    <span className="arc-subtle" style={{ fontWeight: 400 }}>
                      {' '}— all {n.assertions.length} passing
                    </span>
                  )}
                </div>
                {/*
                  Only the FAILING ones by default. These are seven specific,
                  actionable checks — read model reachable, rtpengine kernel
                  module, RTP/ephemeral port overlap, clock skew — and listing
                  seven green ticks every time is how the one red one stops being
                  noticed. They were previously visible only over loopback on the
                  box itself.
                */}
                {n.failing_assertions > 0 && (
                  <ul style={{ margin: '6px 0 0', padding: 0, listStyle: 'none' }}>
                    {n.assertions.filter((a) => !a.ok).map((a) => (
                      <li key={a.id} style={{ fontSize: 12.5, padding: '2px 0' }}>
                        <span style={{ color: 'var(--arc-danger)', fontWeight: 550 }}>{a.name}</span>
                        <span className="arc-subtle"> — {a.detail}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </>
            )}
          </div>
        </div>
      ))}

      <p className="arc-hint">
        Read-only by design. There is no inbound path to a node and nothing
        consumes a node&rsquo;s status, so a drain or restart control here would
        report success without doing anything.
      </p>
    </>
  );
}
